import { useLocation } from "react-router";
import { useState, useEffect, useCallback } from "react";

import CasinoTableLayout from "../../casinoComponents/casinoTableLayout";
import CasinoHeading from "../../casinoComponents/casinoHeading";
import BallByBallVideo from "./BallByBallVideo";
import BallByBallMarket from "./BallByBallMarket";
import ResultBallByBall from "./ResultBallByBall";
import CasinoLastResult from "../../casinoComponents/casinoLastResult";
import MyBet from "../../casinoComponents/myBet";
import ViewMoreModal from "../../../components/ViewMoreModal";
import {
  getCasinoGameDetails,
  getMatchExposure,
  getCasinoOpenBets,
} from "../../../apiservices/CasionApi";

import { extractCasinoGame } from "../../../utils/casinoGame";
// lucky15.css holds the shared .casino-table.ball-by-ball fixes (Market6, ball popup); ballbyball.css the rest
import "../../../styles/games/lucky15.css";
import "../../../styles/games/ballbyball.css";

// Reference .game-header title and .casino-table classes (casino/ballbyball)
const TITLE = "Ball By Ball";
const TABLE_CLASS = "five-cricket super-over detail-page-container ball-by-ball";


export default function BallByBall() {
  const [gameData, setGameData] = useState(null);
  const [exposures, setExposures] = useState({});
  const [myBets, setMyBets] = useState([]);
  const [showViewMoreModal, setShowViewMoreModal] = useState(false);

  const { pathname } = useLocation();
  const gameName = extractCasinoGame(pathname) || "ballbyball";
  const iframeSrc = `https://stream-s-43.uhdmovies.online/casino-stream?id=${gameName}`;
  const matchId = (gameData?.data?.data?.mid || gameData?.data?.mid || gameData?.mid)?.toString();

  useEffect(() => {
    const name = extractCasinoGame(pathname) || "ballbyball";

    const fetchGameData = async () => {
      try {
        const response = await getCasinoGameDetails(name);
        setGameData(response);
      } catch (err) {
        console.error("Error fetching game data: ", err);
      }
    };

    fetchGameData();
    const interval = setInterval(fetchGameData, 1500);
    return () => clearInterval(interval);
  }, [pathname]);

  const fetchExposure = useCallback(async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const userId = user?.user_id || user?.id || user?.staff_id || user?.owner_id;
      if (userId && matchId) {
        const response = await getMatchExposure(userId, matchId);
        if (response?.success && response?.data) {
          const exposureMap = {};
          response.data.forEach((item) => {
            const key = item.team_name || item.selection || item.player_name || "";
            if (key) exposureMap[key] = parseFloat(item.exposure_amount) || 0;
          });
          setExposures(exposureMap);
        }
      }
    } catch (error) {
      console.error("Error fetching exposure:", error);
    }
  }, [matchId]);

  const fetchMyBetsData = useCallback(async () => {
    if (!gameName) return;
    try {
      const response = await getCasinoOpenBets(gameName);
      if (response?.success && Array.isArray(response.data)) {
        const formattedBets = response.data.map((bet) => ({
          // My Bets "Gametype" column (casino_bets.game_name, e.g. mogambo)
          description: bet.description ?? bet.game_name ?? bet.gameName,
          userName: bet.username || bet.userName || "-",
          matchedBet: bet.matchedBet || bet.nation || bet.selection || bet.player_name || "-",
          selection: bet.selection || bet.player_name || bet.matchedBet || "",
          player_name: bet.player_name || "",
          odds: bet.odds ?? bet.userrate ?? "0",
          stake: bet.stake ?? bet.amount ?? "0",
          placeDate: bet.placeDate ?? bet.created_at ?? "-",
          gametype: bet.gametype ?? bet.game_type ?? (bet.type === "lay" ? "Lay" : bet.type === "back" ? "Back" : "-"),
          type: bet.type || null,
          exposer: parseFloat(bet.exposer || bet.exposure_amount || "0") || 0,
        }));
        setMyBets(formattedBets);
      }
    } catch (error) {
      console.error("Error fetching my bets (open-downline):", error);
    }
  }, [gameName]);

  useEffect(() => {
    if (matchId) {
      const t = setTimeout(() => fetchExposure(), 2000);
      const exposureInterval = setInterval(fetchExposure, 2000);
      return () => {
        clearTimeout(t);
        clearInterval(exposureInterval);
      };
    }
  }, [fetchExposure, matchId]);

  useEffect(() => {
    const firstId = setTimeout(fetchMyBetsData, 0);
    const intervalId = setInterval(fetchMyBetsData, 3000);
    return () => {
      clearTimeout(firstId);
      clearInterval(intervalId);
    };
  }, [fetchMyBetsData]);

  const resultData =
    gameData?.lrs ?? gameData?.data?.data?.lrs ?? gameData?.data?.lrs ?? [];

  const roundId =
    gameData?.data?.data?.mid || gameData?.data?.mid || gameData?.mid || "";

  const feed = gameData?.data?.data ?? {};
  const remark = feed?.remark;

  // Reference markets: sub filtered by subtype (runs / wicket / extraruns), each ordered by sr
  const bySubtype = (t) =>
    (Array.isArray(feed?.sub) ? feed.sub : [])
      .filter((s) => s?.subtype === t)
      .sort((a, b) => Number(a?.sr) - Number(b?.sr));
  const runs = bySubtype("runs");
  const wicket = bySubtype("wicket");
  const extraruns = bySubtype("extraruns");

  // Admin panel places no bets: an odds click just refreshes book + My Bets
  const handleBetClick = () => {
    fetchExposure();
    fetchMyBetsData();
  };

  return (
    <CasinoTableLayout
      tableClass={TABLE_CLASS}
      rightSide={
        <MyBet bets={myBets} onViewMore={() => setShowViewMoreModal(true)} />
      }
    >
      <>
        <CasinoHeading name={TITLE} roundId={roundId} />{" "}
        <BallByBallVideo
          iframeSrc={iframeSrc}
          timerValue={feed?.lt ?? gameData?.lt}
          rdesc={feed?.rdesc}
          gameType={gameName}
        />{" "}
        {feed?.mid ? (
          <div className="market-container">
            {runs.length ? (
              <BallByBallMarket data={runs} title="Runs" index="-runs" exposures={exposures} onBetClick={handleBetClick} />
            ) : null}{" "}
            {wicket.length ? (
              <BallByBallMarket data={wicket} title="Wicket" index="-wicket" exposures={exposures} onBetClick={handleBetClick} />
            ) : null}{" "}
            {extraruns.length ? (
              <BallByBallMarket data={extraruns} title="Extra Runs" index="-extraruns" exposures={exposures} onBetClick={handleBetClick} />
            ) : null}
          </div>
        ) : null}{" "}
        {remark && remark !== "" ? (
          <div className="casino-remark mt-3">
            <div className="remark-icon">
              <img src={`${import.meta.env.BASE_URL}assets/img/icons/remark.png`} alt="" />
            </div>{" "}
            <marquee>{remark}</marquee>
          </div>
        ) : null}{" "}
        <CasinoLastResult
          gameType={gameName}
          data={resultData}
          gameData={gameData}
          viewAllLink={`/admin/reports/casinoresult/${gameName}`}
          resultComponent={<ResultBallByBall gameType={gameName} data={resultData} />}
        />
      </>
      <ViewMoreModal
        show={showViewMoreModal}
        onHide={() => setShowViewMoreModal(false)}
        title="View More"
        sectionKey={gameName}
      />
    </CasinoTableLayout>
  );
}
